'use client';

import { useEffect, useRef } from 'react';

import {
    buildDeliveryAvailability,
    getNavigationId,
    getNavigationName,
    getSearchStats,
    processFindingMethod,
    setSkuSearchTerm,
    validateStockStatus,
} from '@libs/analytics';
import { decodeFref, getENV, NavigationListTypes, useDecisions } from '@libs/common-next';
import type { DeliveryMethodType } from '@libs/common-types';

import { isProductAvailableForAutoship, useProduct } from '@/entities/product';
import { usePurchaseFormValue } from '@/entities/purchase-form';
import type {
    ViewItemBaseItem,
    ViewItemCrawlEvent,
    ViewItemEcommerce,
    ViewItemPrecrawlEvent,
} from '@/shared/analytics';
import { dataLayerPush } from '@/shared/analytics';
import { useAutoshipSubscription } from '@/widgets/product-details/subscribed-with-autoship';

export const useViewItem = () => {
    const hasFiredRef = useRef(false);

    const { product, base, tailored, isLoading } = useProduct();
    const autoshipFlag = useDecisions('ff_autoship');
    const isSubscribedIndicatorEnabled = useDecisions('x_as_sub_ind');
    const { isReady, isSubscribed } = useAutoshipSubscription();

    const getValues = usePurchaseFormValue();
    const { purchaseOption, fulfilmentMethod } = getValues();

    useEffect(() => {
        if (
            hasFiredRef.current ||
            isLoading ||
            !isReady ||
            !product ||
            !base ||
            !tailored ||
            !purchaseOption ||
            !fulfilmentMethod
        )
            return;

        const upperFulfilmentMethod = fulfilmentMethod.toUpperCase() as DeliveryMethodType;

        const { categories, merchandisingHierarchy } = base;
        const { sku, webStyleId, name, brand, variantValues, deliveryMethods } = product;

        const price = product.price.sale ?? product.price.original ?? 0.0;
        const frefData = decodeFref();
        const trackingParam = processFindingMethod(window.sessionStorage.pdpFindingMethod);

        const { searchTerm } = getSearchStats();

        const quantity = product?.deliveryMethods[upperFulfilmentMethod]?.purchaseQuantities?.min ?? 1;
        const country = getENV('NEXT_PUBLIC_LOCALE');
        const isCA = country.endsWith('CA');

        const hasExistingSubscription = Boolean(isSubscribedIndicatorEnabled) && isSubscribed;

        const itemListId = frefData?.item_list_id ?? getNavigationId(trackingParam);
        const itemListName = frefData?.item_list_name ?? getNavigationName(trackingParam);
        const isFromSearch = itemListId === NavigationListTypes.SEARCH;
        const itemSearchTerm = isFromSearch ? searchTerm : null;

        setSkuSearchTerm(sku, itemSearchTerm);

        const baseItemData: ViewItemBaseItem = {
            autoship_autoselected: 'no',
            autoship_availability: isProductAvailableForAutoship(product) ? 'yes' : 'no',
            color: variantValues?.color ?? '',
            default_version: sku === base.defaultSku ? 'yes' : 'no',
            existing_subscription: hasExistingSubscription ? 'yes' : 'no',
            flavor: variantValues?.flavor ?? '',
            item_brand: brand?.name ?? '',
            item_id: webStyleId,
            ...(frefData && { index: frefData.index }),
            item_list_id: itemListId,
            item_list_name: itemListName,
            item_name: name,
            item_search_term: itemSearchTerm,
            item_variant: sku,
            price,
            item_category: merchandisingHierarchy?.vicePresidentName ?? '',
            item_category2: merchandisingHierarchy?.division ?? '',
            item_category3: merchandisingHierarchy?.department ?? '',
            item_category4: merchandisingHierarchy?.class ?? '',
            item_category5: merchandisingHierarchy?.category ?? '',
            quantity,
            size: variantValues?.size ?? '',
        };

        const baseEcommerceData = {
            category: categories[0] ?? '',
            currency: isCA ? 'CAD' : 'USD',
            pdp_type: 'standard pdp',
            sub_category1: categories[1] ?? '',
            sub_category2: categories[2] ?? '',
            value: price * quantity,
            items: [],
        } satisfies ViewItemEcommerce<ViewItemBaseItem>;

        const isCustomPharmacyOOS = Boolean(product?.pharmacy?.oos);

        if (autoshipFlag === 'precrawl') {
            const availableStock = tailored.variants[sku]?.[upperFulfilmentMethod]?.availableStock ?? 0;
            const stockStatusText = validateStockStatus({
                availableStock,
                isCustomPharmacyOOS,
                minimumCaseQty: quantity,
            });

            const precrawlEvent: ViewItemPrecrawlEvent = {
                event: 'view_item',
                ecommerce: {
                    ...baseEcommerceData,
                    items: [
                        {
                            ...baseItemData,
                            autoship_stock_availability: stockStatusText,
                        },
                    ],
                },
            };

            dataLayerPush(precrawlEvent);

            hasFiredRef.current = true;

            return;
        }

        const deliveryMethodData = buildDeliveryAvailability({
            deliveryMethods,
            tailoredVariant: tailored.variants[sku],
            purchaseOption,
            isCustomPharmacyOOS,
        });

        const crawlEvent: ViewItemCrawlEvent = {
            event: 'view_item',
            ecommerce: {
                ...baseEcommerceData,
                items: [
                    {
                        ...baseItemData,
                        ...deliveryMethodData,
                    },
                ],
            },
        };

        dataLayerPush(crawlEvent);

        hasFiredRef.current = true;
    }, [
        product,
        base,
        tailored,
        isLoading,
        isReady,
        isSubscribed,
        autoshipFlag,
        isSubscribedIndicatorEnabled,
        purchaseOption,
        fulfilmentMethod,
    ]);
};
